import { Users, CalendarCheck, Check, ArrowRight } from "lucide-react";
import { useSite, useSeo } from "@/context/SiteContext";
import { has } from "@/lib/api";
import { Section, SectionHead, ApartmentCard, CtaBanner, PageHero } from "@/components/public/Shared";
import { Link, useT } from "@/i18n";

export default function GroupStay() {
  const t = useT();
  const { apartments, content } = useSite();
  const g = content.group || {};
  const total = apartments.reduce((s, a) => s + (Number(a.capacity) || 0), 0);
  const title = g.title || t("Séjour en groupe au Chalet Badouille Izoard");
  useSeo({ description: g.intro });
  return (
    <div data-testid="group-page">
      <PageHero eyebrow={g.eyebrow || t("Séjour en groupe")} title={title} text={g.intro} image={g.image_url} testId="group-hero" />

      <Section className="bg-ecru" testId="group-intro">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <SectionHead eyebrow={t("Les 3 appartements réunis")} title={total > 0 ? t("Jusqu'à {n} personnes sous le même toit", { n: total }) : t("Les trois appartements, un même chalet")} />
            {has(g.text) && <p className="mt-6 text-charcoal/75 leading-relaxed whitespace-pre-line" data-testid="group-text">{g.text}</p>}
            {has(g.highlights) && (
              <ul className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="group-highlights">
                {g.highlights.map((h, i) => (
                  <li key={i} className="flex gap-3 bg-white rounded-2xl border border-sand p-4">
                    <span className="w-8 h-8 rounded-full bg-sage-100 text-forest flex items-center justify-center shrink-0"><Check size={16} /></span>
                    <div><p className="font-medium text-forest text-sm">{h.title}</p>{has(h.text) && <p className="text-xs text-charcoal/65 mt-1">{h.text}</p>}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <aside className="lg:col-span-5 card p-7 lg:sticky lg:top-28" data-testid="group-summary">
            <p className="eyebrow">{t("En un coup d'œil")}</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3"><Users size={18} className="text-sage" /> {apartments.length > 1 ? t("{n} appartements indépendants", { n: apartments.length }) : t("{n} appartement indépendant", { n: apartments.length })}</li>
              {total > 0 && <li className="flex items-center gap-3"><Users size={18} className="text-sage" /> {t("Jusqu'à {n} personnes au total", { n: total })}</li>}
              {apartments.map((a) => has(a.capacity) && <li key={a.id} className="flex items-center justify-between border-t border-sand pt-3"><span className="font-semibold text-forest">{a.name}</span><span className="text-charcoal/60">{t("jusqu'à {n} pers.", { n: a.capacity })}</span></li>)}
            </ul>
            <Link to="/demande-de-disponibilite?appartement=all" className="btn-primary w-full mt-6 py-4" data-testid="group-request-cta"><CalendarCheck size={18} /> {t("Demander une disponibilité")}</Link>
            <p className="text-xs text-charcoal/50 mt-3">{t("Demande sans engagement : elle ne constitue pas une réservation. Nous vous confirmons les disponibilités et les modalités du séjour.")}</p>
          </aside>
        </div>
      </Section>

      <Section className="bg-cream" testId="group-apartments">
        <SectionHead eyebrow={t("Le détail")} title={t("Les trois appartements")} align="center" />
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">{apartments.map((a) => <ApartmentCard key={a.id} apt={a} compact highlightCapacity />)}</div>
        {has(g.practical) && <div className="mt-12 max-w-3xl mx-auto bg-white rounded-2xl border border-sand p-7" data-testid="group-practical"><h3 className="h3 mb-3">{t("Organisation pratique")}</h3><p className="text-sm text-charcoal/75 leading-relaxed whitespace-pre-line">{g.practical}</p></div>}
        <div className="text-center mt-10"><Link to="/appartements" className="btn-ghost">{t("Voir tous les appartements")} <ArrowRight size={16} /></Link></div>
      </Section>

      <CtaBanner title={g.cta_title || t("Un séjour à plusieurs au Chalet Badouille Izoard ?")} to="/demande-de-disponibilite?appartement=all" />
    </div>
  );
}
